import { Button } from "../../../../components/Button";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CalendarEventResponse } from "../../../../types/api";
import type { TournamentGameDto } from "../../../../types/tournament-games";
import { getTournamentGames } from "../../../../services/tournament-games";
import { directionsUrl } from "../../../../services/calendar";
import { formatEventClockTime } from "../../../../utils/calendarTime";
import { formatMatchup } from "./gameDisplay";
import {
  defaultDayKey,
  dayForFocus,
  distinctDayKeys,
  gamesOnDay,
  nextGame,
  tabForFocus,
  type ItineraryFilter,
} from "./itineraryPanelView";
import { PrintScheduleLink } from "./print/PrintScheduleLink";
import "./ScheduleSection.css";

interface ItineraryPanelProps {
  /** The parent tournament (its id drives the games fetch; title heads the panel). */
  event: CalendarEventResponse;
  /** A game to open focused on: switch to its tab/day, scroll to it, and ring it. */
  focusGameId?: string;
  /** Swap to the full read-only event view for the same tournament. */
  onViewFullEvent: () => void;
  onClose: () => void;
}

const todayKey = () =>
  new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

/** Directions URL for a game's field, reusing the shared calendar builder. */
const gameDir = (g: TournamentGameDto): string | null =>
  directionsUrl({
    locationPlaceId: g.fieldPlaceId,
    locationLat: g.fieldLat,
    locationLng: g.fieldLng,
    locationAddress: g.fieldAddress,
    locationName: g.fieldName || null,
  });

/** A read-only game line: time, matchup, coverage badge, field, starred, directions. */
const GameLine = ({ game }: { game: TournamentGameDto }) => {
  const dir = gameDir(game);
  const time = formatEventClockTime({
    startsAtUtc: game.startsAtUtc,
    timeZone: game.timeZone,
  });
  const matchup =
    game.homeTeamNameRaw || game.awayTeamNameRaw ? formatMatchup(game) : "TBD";
  return (
    <div className="itinerary-panel__game">
      <div className="itinerary-panel__game-main">
        <span className="itinerary-panel__game-time">{time}</span>
        <span className="itinerary-panel__game-matchup">{matchup}</span>
        {game.isAssignedToMe && (
          <span className="itinerary-panel__mine-badge">You're covering</span>
        )}
      </div>
      <div className="itinerary-panel__game-meta">
        {game.fieldName && <span>📍 {game.fieldName}</span>}
        {game.starredPlayerCount > 0 && (
          <span className="itinerary-panel__starred">★ {game.starredPlayerCount}</span>
        )}
        {dir && (
          <a
            className="itinerary-panel__dir"
            href={dir}
            target="_blank"
            rel="noreferrer"
          >
            Directions
          </a>
        )}
      </div>
    </div>
  );
};

/**
 * Read-only itinerary side panel: a coach's lens over one tournament's games
 * (My schedule / All games), a next-game card, and a per-day schedule. Opened
 * focused on a game from the calendar — it switches to that game's tab + day,
 * scrolls to it, and briefly rings it. A vanished focus id shows a quiet notice.
 */
export const ItineraryPanel = ({
  event,
  focusGameId,
  onViewFullEvent,
  onClose,
}: ItineraryPanelProps) => {
  const [games, setGames] = useState<TournamentGameDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState<ItineraryFilter>("mine");
  // null = use the derived default; a string is an explicit choice.
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [highlightedGameId, setHighlightedGameId] = useState<string | null>(null);
  const [focusNotFound, setFocusNotFound] = useState(false);
  // Remembers the (event, gameId) we already focused so a reload / manual click
  // does not re-apply the focus and fight the user.
  const focusedFor = useRef<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const load = useCallback(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    getTournamentGames(event.id)
      .then((resp) => {
        if (active) setGames(resp.games);
      })
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [event.id]);

  useEffect(() => load(), [load]);

  const filtered = useMemo(
    () => (filter === "mine" ? games.filter((g) => g.isAssignedToMe) : games),
    [games, filter],
  );
  const days = useMemo(() => distinctDayKeys(filtered), [filtered]);
  const activeDay = useMemo(() => {
    if (selectedDay && days.some((d) => d.key === selectedDay)) return selectedDay;
    return defaultDayKey(days, todayKey());
  }, [selectedDay, days]);
  const dayGames = useMemo(
    () => (activeDay ? gamesOnDay(filtered, activeDay) : []),
    [filtered, activeDay],
  );
  const upcoming = useMemo(() => nextGame(filtered, Date.now()), [filtered]);

  // Resolve the deep-linked game once per (event, gameId) load. In range → jump
  // to its tab + day and ring it; absent → show the quiet notice, keep defaults.
  useEffect(() => {
    if (loading || loadError || !focusGameId) return;
    const key = `${event.id}:${focusGameId}`;
    if (focusedFor.current === key) return;
    focusedFor.current = key;

    const t = tabForFocus(games, focusGameId);
    const d = dayForFocus(games, focusGameId);
    if (t && d) {
      setFilter(t);
      setSelectedDay(d);
      setHighlightedGameId(focusGameId);
      setFocusNotFound(false);
    } else {
      setFocusNotFound(true);
    }
  }, [games, focusGameId, event.id, loading, loadError]);

  // Fade the ring after a beat. Kept independent of day/filter navigation so
  // moving around during the window doesn't restart the timer.
  useEffect(() => {
    if (!highlightedGameId) return;
    const timer = window.setTimeout(() => setHighlightedGameId(null), 2500);
    return () => window.clearTimeout(timer);
  }, [highlightedGameId]);

  // Scroll to AND move keyboard/SR focus onto the focused row once it mounts.
  // Re-runs as the day's rows change so it fires after the focused day mounts;
  // a no-op when the highlighted row isn't on the shown day (ref is absent).
  useEffect(() => {
    if (!highlightedGameId) return;
    const el = rowRefs.current.get(highlightedGameId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  }, [highlightedGameId, dayGames]);

  // Any manual navigation dismisses the "game is gone" notice so it doesn't
  // stay pinned for the whole session.
  const chooseFilter = (next: ItineraryFilter) => {
    setFilter(next);
    setFocusNotFound(false);
  };
  const chooseDay = (key: string) => {
    setSelectedDay(key);
    setFocusNotFound(false);
  };

  return (
    <aside
      className="event-editor event-view itinerary-panel"
      aria-label={`Itinerary for ${event.title}`}
    >
      <div className="event-editor__head">
        <div className="event-editor__title">
          <span className="itinerary-panel__eyebrow">Itinerary</span>
          <span className="itinerary-panel__event-title">{event.title}</span>
        </div>
        <ModalCloseButton onClick={onClose} />
      </div>

      <div className="event-editor__body">
        <div
          className="itinerary-panel__filter"
          role="group"
          aria-label="Itinerary filter"
        >
          <button
            type="button"
            className={`itinerary-panel__filter-btn${filter === "mine" ? " itinerary-panel__filter-btn--on" : ""}`}
            aria-pressed={filter === "mine"}
            onClick={() => chooseFilter("mine")}
            data-testid="itinerary-filter-mine"
          >
            My schedule
          </button>
          <button
            type="button"
            className={`itinerary-panel__filter-btn${filter === "all" ? " itinerary-panel__filter-btn--on" : ""}`}
            aria-pressed={filter === "all"}
            onClick={() => chooseFilter("all")}
            data-testid="itinerary-filter-all"
          >
            All games
          </button>
        </div>

        {focusNotFound && (
          <div className="itinerary-panel__notice" data-testid="itinerary-focus-notfound">
            That game is no longer on the schedule.
          </div>
        )}

        <div aria-live="polite">
          {loading && <div className="event-editor__hint">Loading itinerary…</div>}
          {loadError && (
            <div className="event-editor__error" role="alert">
              Couldn't load the itinerary.{" "}
              <button
                type="button"
                className="itinerary-panel__retry"
                onClick={load}
                data-testid="itinerary-retry"
              >
                Retry
              </button>
            </div>
          )}
          {!loading && !loadError && games.length === 0 && (
            <div className="event-editor__hint">
              No games yet. Confirm this tournament to build the itinerary.
            </div>
          )}
        </div>

        {!loading && !loadError && games.length > 0 && (
          <>
            {filter === "mine" && filtered.length === 0 ? (
              <div className="event-editor__hint" data-testid="itinerary-mine-empty">
                None of these games are assigned to you yet. Try All games.
              </div>
            ) : (
              <>
                <div className="itinerary-panel__card">
                  <div className="itinerary-panel__card-title">Next game</div>
                  {upcoming ? (
                    <GameLine game={upcoming} />
                  ) : (
                    <div className="event-editor__hint">No upcoming games.</div>
                  )}
                </div>

                {days.length > 0 && (
                  <div
                    className="itinerary-panel__days"
                    role="group"
                    aria-label="Days"
                  >
                    {days.map((d) => (
                      <button
                        key={d.key}
                        type="button"
                        className={`itinerary-panel__day${d.key === activeDay ? " itinerary-panel__day--on" : ""}`}
                        aria-pressed={d.key === activeDay}
                        onClick={() => chooseDay(d.key)}
                        data-testid={`itinerary-day-${d.key}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                )}

                <div className="itinerary-panel__card">
                  <div className="itinerary-panel__card-title">Schedule</div>
                  {dayGames.length === 0 ? (
                    <div className="event-editor__hint">No games on this day.</div>
                  ) : (
                    dayGames.map((g) => (
                      <div
                        key={g.id}
                        ref={(el) => {
                          if (el) rowRefs.current.set(g.id, el);
                          else rowRefs.current.delete(g.id);
                        }}
                        className={`itinerary-panel__row${g.id === highlightedGameId ? " itinerary-panel__row--focus" : ""}`}
                        data-testid={`itinerary-row-${g.id}`}
                        tabIndex={g.id === highlightedGameId ? -1 : undefined}
                      >
                        <GameLine game={g} />
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="event-editor__foot">
        <div className="event-editor__foot-right">
          <Button variant="secondary"  onClick={onClose}>
            Close
          </Button>
          {/* Prints the tab on screen: My schedule or All games. */}
          <PrintScheduleLink
            request={{ kind: "event", eventId: event.id, games: filter }}
            data-testid="itinerary-print"
          />
          <Button variant="primary"
            type="button"
            onClick={onViewFullEvent}
            data-testid="itinerary-view-full"
          >
            View full event
          </Button>
        </div>
      </div>
    </aside>
  );
};
